import { Avatar, AvatarFallback } from './ui/avatar';
import { PlanStatusNavbar } from './PlanStatusNavbar';
import { useTheme, themes } from './ThemeProvider';
import { useAuthContext } from '../contexts/AuthContext';
import { useAppSelector } from '../store/store';
import { API_URL } from '../lib/api-client';
import { useUserDisplay } from '../hooks/useUserDisplay';
import { useState } from 'react';
import {
  Bell,
  Heart,
  MessageCircle,
  Users,
  Crown,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Search,
  Sparkles,
  Gift,
  HelpCircle,
  UserCircle,
  Activity,
  Bookmark,
  Shield,
  Video,
  BarChart3,
  CreditCard
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { AtootbandanLogo } from './AtootbandanLogo';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface DesktopNavbarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onNavigate: (screen: string) => void;
  onLogout: () => void;
  onShowNotifications: () => void;
  onShowProfile: () => void;
  onShowSettings: () => void;
  settings?: any; // Admin-controlled feature flags
}

export default function DesktopNavbar({
  activeTab,
  onTabChange,
  onNavigate,
  onLogout,
  onShowNotifications,
  onShowProfile,
  onShowSettings,
  settings
}: DesktopNavbarProps) {
  const { user } = useAuthContext();
  const notificationCount = useAppSelector(state => state?.notification?.unreadCount || 0);
  const { conversations } = useAppSelector(state => state?.chat || { conversations: [] });
  const { currentSubscription } = useAppSelector(state => state?.plan || { currentSubscription: null });
  const [showPlanStatus, setShowPlanStatus] = useState(false);
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  const messageCount = conversations?.filter((conv: any) => (conv?.unreadCount || 0) > 0).length || 0;

  const getProfilePhoto = () => {
    // Priority 1: Direct profilePhoto URL (S3 URL from DB)
    if (user?.profilePhoto) {
      return user.profilePhoto;
    }
    
    // Priority 2: Photos array with url objects
    if (user?.photos?.length > 0) {
      const photo = user.photos[0];
      // Check if photos[0] is an object with url field
      if (typeof photo === 'object' && photo?.url) {
        return photo.url;
      }
      // Fallback: if photos[0] is a string (old format)
      if (typeof photo === 'string') {
        // Check if it's already a full URL
        if (photo.startsWith('http')) {
          return photo;
        }
        return `${API_URL.replace('/api', '')}/uploads/${photo}`;
      }
    }
    
    return null;
  };

  const getInitials = () => {
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const isPremium = currentSubscription?.status === 'active';

  // Dynamically filter nav items based on admin settings
  const allNavItems = [
    ...(settings?.features?.discoverTabEnabled !== false ? [
      { id: 'matches', label: 'Discover', icon: Users }
    ] : []),
    ...(settings?.features?.activityTabEnabled !== false ? [
      { id: 'activity', label: 'Activity', icon: Activity }
    ] : []),
    ...(settings?.features?.messagesTabEnabled !== false ? [
      { id: 'messages', label: 'Messages', icon: MessageCircle, badge: messageCount }
    ] : []),
    ...(settings?.features?.liveTabEnabled ? [
      { id: 'live', label: 'Live', icon: Video }
    ] : []),
  ];

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="w-full px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-8">
            <div className="cursor-pointer" onClick={() => onNavigate('home')}>
              <AtootbandanLogo size="sm" showText={true} />
            </div>

            {/* Main Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {allNavItems.map((item) => (
                <Button
                  key={item.id}
                  variant={activeTab === item.id ? 'secondary' : 'ghost'}
                  className={`relative ${
                    activeTab === item.id
                      ? 'bg-pink-50 text-pink-600 hover:bg-pink-100'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  onClick={() => onTabChange(item.id)}
                >
                  <item.icon className="size-4 mr-2" />
                  {item.label}
                  {item.badge && item.badge > 0 && (
                    <Badge className="ml-2 bg-pink-500 text-white h-5 min-w-5 flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </Badge>
                  )}
                </Button>
              ))}
            </div>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {/* Plan Status Button */}
            <Button
              variant="ghost"
              size="sm"
              className={`relative ${currentTheme.primaryText} hover:${currentTheme.light}`}
              onClick={() => setShowPlanStatus(true)}
              title="View Plan Status"
            >
              <CreditCard className="size-5" />
            </Button>

            {/* Upgrade to Premium */}
            {!isPremium && settings?.features?.plansTabEnabled !== false && (
              <Button
                size="sm"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white"
                onClick={() => onTabChange('plans')}
              >
                <Crown className="size-4 mr-2" />
                Upgrade
              </Button>
            )}

            {/* Notifications */}
            <Button
              variant="ghost"
              size="sm"
              className="relative"
              onClick={onShowNotifications}
            >
              <Bell className="size-5" />
              {notificationCount > 0 && (
                <Badge className="absolute -top-1 -right-1 bg-red-500 text-white h-5 min-w-5 flex items-center justify-center text-xs">
                  {notificationCount > 9 ? '9+' : notificationCount}
                </Badge>
              )}
            </Button>

            {/* Admin Panel Button - Only for Admin Users */}
            {user?.role === 'admin' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                onClick={() => onNavigate('admin')}
                title="Admin Panel"
              >
                <Shield className="size-5" />
              </Button>
            )}

            {/* Marketing Manager Panel Button - Only for Marketing Managers */}
            {user?.role === 'marketing_manager' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                onClick={() => onNavigate('marketing-manager')}
                title="Marketing Manager Panel"
              >
                <BarChart3 className="size-5" />
              </Button>
            )}

            {/* Marketing Panel Button - Only for Marketing Users */}
            {(user?.role === 'marketing' || user?.role === 'marketing_user') && (
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => onNavigate('marketing-panel')}
                title="Marketing Panel"
              >
                <BarChart3 className="size-5" />
              </Button>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 hover:bg-gray-100">
                  <div className="relative">
                    {getProfilePhoto() ? (
                      <ImageWithFallback
                        src={getProfilePhoto()!}
                        alt={user?.firstName || 'User'}
                        className="size-8 rounded-full object-cover"
                      />
                    ) : (
                      <Avatar className="size-8">
                        <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                    )}
                    {isPremium && (
                      <div className="absolute -bottom-1 -right-1 bg-yellow-500 rounded-full p-0.5">
                        <Crown className="size-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="hidden lg:block text-left">
                    <div className="text-sm font-medium">
                      {user?.firstName} {user?.lastName}
                    </div>
                    {isPremium && (
                      <div className="text-xs text-yellow-600 flex items-center gap-1">
                        <Crown className="size-3" />
                        Premium Member
                      </div>
                    )}
                  </div>
                  <ChevronDown className="size-4 text-gray-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={onShowProfile}>
                  <User className="size-4 mr-2" />
                  View Profile
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => onNavigate('edit-profile')}>
                  <UserCircle className="size-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => onTabChange('activity')}>
                  <Heart className="size-4 mr-2" />
                  My Interests
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => {
                  // Navigate to activity tab - the ActivityTab component will handle showing shortlisted
                  onTabChange('activity');
                  // Store preference to show shortlisted tab
                  sessionStorage.setItem('activityTabFilter', 'shortlisted');
                }}>
                  <Bookmark className="size-4 mr-2" />
                  Shortlisted
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                {!isPremium && settings?.features?.plansTabEnabled !== false && (
                  <DropdownMenuItem onClick={() => onTabChange('plans')}>
                    <Crown className="size-4 mr-2 text-yellow-600" />
                    <span className="text-yellow-600">Upgrade to Premium</span>
                  </DropdownMenuItem>
                )}
                
                <DropdownMenuItem onClick={() => onNavigate('refer-earn')}>
                  <Gift className="size-4 mr-2" />
                  Refer & Earn
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={onShowSettings}>
                  <Settings className="size-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => onNavigate('help')}>
                  <HelpCircle className="size-4 mr-2" />
                  Help & Support
                </DropdownMenuItem>
                
                <DropdownMenuItem onClick={() => onNavigate('terms')}>
                  <Shield className="size-4 mr-2" />
                  Privacy & Terms
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <LogOut className="size-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Plan Status Panel */}
      <PlanStatusNavbar
        isOpen={showPlanStatus}
        onClose={() => setShowPlanStatus(false)}
        onNavigateToPlans={() => {
          setShowPlanStatus(false);
          onTabChange('plans');
        }}
      />
    </nav>
  );
}